import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  MASS_OS_SERVICE_TYPE_OPTIONS,
  MassOsItineraryPaymentState,
  MassOsPaymentDateStatus,
  MassOsServicePaymentState,
  MassOsTicketPaymentState
} from 'src/entities/file';

interface SyncHeaders {
  serviceDay: string;
  fecha: string;
  tipoServicio: string;
  nombreServicio: string;
  nombre: string;
  moneda: string;
  pax: string;
  costo: string;
  fechaAutomatica: string;
  fechaManual: string;
  fechaFinal: string;
  status: string;
  codigoReserva: string;
}

@Component({
  selector: 'app-mass-os-itinerary-services-sync',
  templateUrl: './mass-os-itinerary-services-sync.component.html',
  styleUrls: ['./mass-os-itinerary-services-sync.component.css']
})
export class MassOsItineraryServicesSyncComponent implements OnChanges {
  @Input() state!: MassOsItineraryPaymentState;
  @Input() step = 0;

  private expandedServiceIds = new Set<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['state'] && this.state) {
      for (const svc of this.state.serviceStates) {
        // Solo los servicios TICKET tienen detalle expandible (sub-tabla de
        // tickets). Los no-ticket muestran sus campos inline en la misma fila.
        const id = svc.serviceId;
        if (this.hasTickets(svc)) {
          this.expandedServiceIds.add(id);
        } else {
          this.expandedServiceIds.delete(id);
        }
      }
    }
  }

  isExpanded(svc: MassOsServicePaymentState): boolean {
    return this.expandedServiceIds.has(svc.serviceId);
  }

  toggle(svc: MassOsServicePaymentState): void {
    if (this.expandedServiceIds.has(svc.serviceId)) {
      this.expandedServiceIds.delete(svc.serviceId);
    } else {
      this.expandedServiceIds.add(svc.serviceId);
    }
  }

  @Output() dateChange = new EventEmitter<{
    serviceId: string;
    date: Date | null;
    ticketId?: string | null;
  }>();

  @Output() codeChange = new EventEmitter<{
    serviceId: string;
    code: string;
    ticketId?: string | null;
  }>();

  private readonly baseColumns = [
    'expand',
    'serviceDay',
    'fecha',
    'tipoServicio',
    'nombreServicio',
    'nombre',
    'moneda',
    'pax',
    'costo'
  ];

  get displayedColumns(): string[] {
    // Las columnas de fecha/código/estado van inline en la fila principal y solo
    // aplican a servicios NO-ticket (los TICKET las ven dentro de su sub-tabla).
    return this.step === 0
      ? [
          ...this.baseColumns,
          'fechaAutomatica',
          'fechaManual',
          'fechaFinal',
          'status'
        ]
      : [...this.baseColumns, 'codigoReserva'];
  }

  /** Solo los servicios con tickets generan una fila de detalle expandible. */
  isTicketRow = (_index: number, svc: MassOsServicePaymentState): boolean =>
    this.hasTickets(svc);

  readonly ticketBaseColumns: string[] = ['ticketId', 'moneda', 'total'];

  get ticketDisplayedColumns(): string[] {
    return this.step === 0
      ? [
          ...this.ticketBaseColumns,
          'fechaAutomatica',
          'fechaManual',
          'fechaFinal',
          'status'
        ]
      : [...this.ticketBaseColumns, 'codigoReserva'];
  }

  readonly headers: SyncHeaders = {
    serviceDay: 'DÍA',
    fecha: 'FECHA SV',
    tipoServicio: 'TIPO DE SERVICIO',
    nombreServicio: 'SERVICIO',
    nombre: 'PROVEEDOR',
    moneda: 'MONEDA',
    pax: 'PAX',
    costo: 'MONTO',
    fechaAutomatica: 'FECHA AUTO',
    fechaManual: 'NUEVA FECHA',
    fechaFinal: 'FECHA PAGO FINAL',
    status: 'ESTADO',
    codigoReserva: 'CÓDIGO RESERVA'
  };

  tipoIcon(tipo: string): string {
    const option = MASS_OS_SERVICE_TYPE_OPTIONS.find(
      (item) => item.label.toLowerCase() === tipo.toLowerCase()
    );
    return option?.icon ?? 'dots';
  }

  hasTickets(svc: MassOsServicePaymentState): boolean {
    return !!(svc.isTicket && svc.tickets?.length);
  }

  getFinalDate(svc: MassOsServicePaymentState): Date {
    if (svc.fechaManual) return svc.fechaManual;
    if (this.state?.fechaMasiva) return this.state.fechaMasiva;
    return svc.fechaAutomatica;
  }

  getTicketFinalDate(ticket: MassOsTicketPaymentState): Date {
    if (ticket.fechaManual) return ticket.fechaManual;
    if (this.state?.fechaMasiva) return this.state.fechaMasiva;
    return ticket.fechaAutomatica;
  }

  getStatusLabel(status: MassOsPaymentDateStatus): string {
    switch (status) {
      case 'modificado':
        return 'Modificado';
      case 'cambio-masivo':
        return 'Cambio masivo';
      default:
        return 'Automático';
    }
  }

  getStatusClass(status: MassOsPaymentDateStatus): string {
    switch (status) {
      case 'modificado':
        return 'sync-status--modified';
      case 'cambio-masivo':
        return 'sync-status--massive';
      default:
        return 'sync-status--auto';
    }
  }

  formatShortDate(date: Date): string {
    return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
  }

  onDateChange(
    serviceId: string,
    date: Date | null,
    ticketId?: string | null
  ): void {
    this.dateChange.emit({ serviceId, date, ticketId });
  }

  onCodeChange(
    serviceId: string,
    code: string,
    ticketId?: string | null
  ): void {
    this.codeChange.emit({ serviceId, code, ticketId });
  }

  onTicketDateChange(svc: MassOsServicePaymentState, ticketId: string, date: Date | null): void {
    this.onDateChange(svc.serviceId, date, ticketId);
  }

  onTicketCodeChange(svc: MassOsServicePaymentState, ticketId: string, code: string): void {
    this.onCodeChange(svc.serviceId, code, ticketId);
  }

  trackByService = (_index: number, svc: MassOsServicePaymentState): string =>
    svc.serviceId;
}
